import React, { Fragment, ReactNode, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import cl from 'classnames';
import isFunction from 'lodash/isFunction';

import { IconsEnum } from '../../../assets/icons/types';
import {
  InContentModalCommonProps,
  InContentModalRenderButtonsPropArgs,
  InContentModalRenderPropArgs
} from './InContentModal.types';

import { Icon } from '../../Icon';
import { Translate } from '../../Translate';
import { SimpleModalButtons } from '../SimpleModal/components/SimpleModalButtons';

import { InContentModalWrapper } from './helpers/InContentModalWrapper';
import { PureIconButtonHelper } from '../../buttons/PureIconButtonHelper';

import { words } from '../../../locales/keys';

interface InContentModalChildrenProps {
  children: ReactNode | ((args: InContentModalRenderPropArgs) => ReactNode);
  childrenButtons?: ReactNode;
  renderButtons?: (args: InContentModalRenderButtonsPropArgs) => ReactNode;
}

interface InContentModalDefaultProps {
  handleSubmit?: () => void;
  hideModal: () => void;
  isOpen: boolean;
}

type InContentModalProps = InContentModalDefaultProps &
  InContentModalCommonProps &
  InContentModalChildrenProps;

function InContentModal({
  addDialogClassName,
  addModalClassName,
  allowPropagation,
  buttonsContainerClassName,
  cancelButtonPosition,
  children,
  childrenButtons,
  childrenClassName,
  closeOnCancel,
  dataGa,
  disabled,
  handleSubmit,
  hideModal,
  i18nCancelText = words.cancel,
  i18nCloseText = words.close,
  i18nSubmitText = words.save,
  i18nTitle,
  id,
  isLoading,
  isOpen = false,
  modalIcon,
  modalIconClassName,
  modalSize = 'xl',
  onCancel,
  renderButtons,
  submitButtonColor = 'blue',
  submitClass,
  submitDisabled,
  submitIcon,
  submitIconClassName,
  title,
  withCloseButton = false,
  withoutButtons = false,
  withoutCancelButton = false,
  withoutChildrenWrapper = false,
  withoutInitialFocus,
  withoutSubmitButton = false
}: InContentModalProps) {
  const submitButtonRef = useRef<HTMLButtonElement | null>(null);
  const divRef = useRef<HTMLDivElement | null>(null);

  const childrenNode =
    typeof children === 'function' ? children({ isOpen, hideModal }) : children;

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as={InContentModalWrapper}
        allowPropagation={allowPropagation}
        className={cl('absolute z-20 inset-0', addDialogClassName)}
        id={id}
        initialFocus={withoutInitialFocus ? divRef : submitButtonRef}
        open={isOpen}
        data-archi-modal
        onClose={hideModal}
      >
        <div
          className="relative flex items-center justify-center h-full p-4"
          ref={divRef}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-transparent transition-opacity" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              className={cl(
                'z-10 flex flex-col bg-white dark:bg-gray-850 border dark:border-gray-800 border-transparent rounded-3xl shadow-xl',
                {
                  'max-h-full w-full sm:max-w-xl': modalSize === 'xl',
                  'max-h-full w-full sm:max-w-xs': modalSize === 'xs',
                  'max-h-full w-full sm:max-w-sm': modalSize === 'sm',
                  'max-h-full w-full sm:max-w-3xl': modalSize === '3xl',
                  'max-h-full w-full sm:max-w-4xl': modalSize === '4xl',
                  'max-h-full w-full sm:max-w-md': modalSize === 'md'
                },
                addModalClassName
              )}
            >
              <div className="flex-shrink px-4">
                {i18nTitle || title ? (
                  <div className="flex justify-between items-center border-b border-gray-300 dark:border-gray-700 py-4">
                    <Fragment>
                      {modalIcon ? (
                        <Icon className={modalIconClassName} icon={modalIcon} />
                      ) : null}
                      <Dialog.Title
                        as="h3"
                        className={cl(
                          'text-base sm:text-lg text-center flex-1 truncate',
                          {
                            'pl-8': !modalIcon
                          }
                        )}
                      >
                        {i18nTitle ? <Translate id={i18nTitle} /> : title}
                      </Dialog.Title>
                      <PureIconButtonHelper
                        dataGa={`${dataGa}-close-button`}
                        className="-mr-2 -my-2 ml-2 dark:hover:bg-gray-700 dark:hover:text-gray-200 dark:text-gray-500 hover:bg-gray-200 hover:text-gray-600 p-2 rounded-full text-gray-400"
                        icon={IconsEnum.X}
                        onClick={hideModal}
                      />
                    </Fragment>
                  </div>
                ) : null}
              </div>

              {withoutChildrenWrapper ? (
                childrenNode
              ) : (
                <div className={childrenClassName || 'p-4'}>{childrenNode}</div>
              )}

              {isFunction(renderButtons)
                ? renderButtons({ handleSubmit, hideModal })
                : null}

              {withoutButtons || isFunction(renderButtons) ? null : (
                <SimpleModalButtons
                  dataGa={dataGa}
                  disabled={disabled}
                  handleSubmit={handleSubmit}
                  i18nCancelText={i18nCancelText}
                  i18nSubmitText={i18nSubmitText}
                  i18nCloseText={i18nCloseText}
                  submitIcon={submitIcon}
                  containerClassName={buttonsContainerClassName}
                  submitClass={submitClass}
                  submitIconClassName={submitIconClassName}
                  submitDisabled={submitDisabled}
                  submitButtonColor={submitButtonColor}
                  cancelButtonPosition={cancelButtonPosition}
                  withoutCancelButton={withoutCancelButton}
                  withCloseButton={withCloseButton}
                  withoutSubmitButton={withoutSubmitButton}
                  isLoading={isLoading}
                  onCancel={onCancel}
                  closeOnCancel={closeOnCancel}
                  hideModal={hideModal}
                  ref={submitButtonRef}
                  childrenButtons={childrenButtons}
                />
              )}
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default InContentModal;
